import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Chip,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import {
    AccountBalance as BankIcon,
    CheckCircle as VerifiedIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    MoreVert as MoreIcon,
    Star as PrimaryIcon,
    StarBorder as NotPrimaryIcon,
    Block as DeactivateIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import API from '../../../../../API';

/**
 * Get status chip configuration based on verification status
 */
function getStatusChip(status, isActive) {
    if (!isActive) {
        return {
            label: 'Inactive',
            color: 'default',
            icon: <ErrorIcon fontSize="small" />
        };
    }

    switch (status) {
        case 'verified':
            return {
                label: 'Verified',
                color: 'success',
                icon: <VerifiedIcon fontSize="small" />
            };
        case 'pending':
            return {
                label: 'Pending',
                color: 'warning',
                icon: <WarningIcon fontSize="small" />
            };
        case 'failed':
            return {
                label: 'Failed',
                color: 'error',
                icon: <ErrorIcon fontSize="small" />
            };
        default:
            return {
                label: status || 'Unknown',
                color: 'default',
                icon: <WarningIcon fontSize="small" />
            };
    }
}

/**
 * Format date for display
 */
function formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return 'Invalid Date';
    }
}

/**
 * Bank Account Card Component
 *
 * Displays individual bank account information with actions.
 */
function BankAccountCard({ account, companyId, onUpdate }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const queryClient = useQueryClient();

    const statusChip = getStatusChip(account.verification_status, account.is_active);

    // Deactivate account mutation
    const deactivateMutation = useMutation(
        () => API.admin.deactivateCompanyBankAccount(account.bank_model_id),
        {
            onSuccess: () => {
                toast.success('Bank account deactivated');
                queryClient.invalidateQueries(['companyBankAccounts']);
                onUpdate();
            },
            onError: (error) => {
                toast.error(`Failed to deactivate: ${error.message}`);
            }
        }
    );

    // Reactivate account mutation
    const reactivateMutation = useMutation(
        () => API.admin.updateCompanyBankAccount(account.bank_model_id, { is_active: true }),
        {
            onSuccess: () => {
                toast.success('Bank account reactivated');
                queryClient.invalidateQueries(['companyBankAccounts']);
                onUpdate();
            },
            onError: (error) => {
                toast.error(`Failed to reactivate: ${error.message}`);
            }
        }
    );

    // Set as primary mutation
    const setPrimaryMutation = useMutation(
        () => API.admin.updateCompanyBankAccount(account.bank_model_id, { is_primary: true }),
        {
            onSuccess: () => {
                toast.success('Set as primary bank account');
                queryClient.invalidateQueries(['companyBankAccounts']);
                onUpdate();
            },
            onError: (error) => {
                toast.error(`Failed to set as primary: ${error.message}`);
            }
        }
    );

    // Delete account mutation (hard delete)
    const deleteMutation = useMutation(
        () => API.admin.deleteCompanyBankAccount(account.bank_model_id),
        {
            onSuccess: () => {
                toast.success('Bank account deleted permanently');
                queryClient.invalidateQueries(['companyBankAccounts']);
                onUpdate();
            },
            onError: (error) => {
                toast.error(`Failed to delete: ${error.message}`);
            }
        }
    );

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        setConfirmAction(action);
        setConfirmDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirm = () => {
        switch (confirmAction) {
            case 'deactivate':
                deactivateMutation.mutate();
                break;
            case 'reactivate':
                reactivateMutation.mutate();
                break;
            case 'setPrimary':
                setPrimaryMutation.mutate();
                break;
            case 'delete':
                deleteMutation.mutate();
                break;
            default:
                break;
        }
        setConfirmDialogOpen(false);
        setConfirmAction(null);
    };

    const handleCancel = () => {
        setConfirmDialogOpen(false);
        setConfirmAction(null);
    };

    const getConfirmDialogContent = () => {
        switch (confirmAction) {
            case 'deactivate':
                return {
                    title: 'Deactivate Bank Account?',
                    message: 'This will deactivate the bank account. ACH reimbursements will not use this account. You can reactivate it later if needed.'
                };
            case 'reactivate':
                return {
                    title: 'Reactivate Bank Account?',
                    message: 'This will reactivate the bank account.'
                };
            case 'setPrimary':
                return {
                    title: 'Set as Primary Account?',
                    message: 'This will set this account as the primary bank account for ACH reimbursements.'
                };
            case 'delete':
                return {
                    title: 'Delete Bank Account?',
                    message: 'This will permanently delete the bank account. This action cannot be undone. Are you sure you want to continue?'
                };
            default:
                return { title: '', message: '' };
        }
    };

    const dialogContent = getConfirmDialogContent();

    return (
        <>
            <Card
                elevation={account.is_active ? 3 : 1}
                sx={{
                    height: '100%',
                    border: account.is_active ? 2 : 0,
                    borderColor: 'primary.main',
                    opacity: account.is_active ? 1 : 0.7
                }}
            >
                <CardContent>
                    {/* Header with Bank Icon and Status */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BankIcon sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                            <Box>
                                <Typography variant="h6" component="div">
                                    {account.bank_name || 'Unknown Bank'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {account.account_type || 'Unknown'} Account
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton size="small" onClick={handleMenuOpen}>
                            <MoreIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Account Details */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Account:</strong> {account.account_last_4}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Routing:</strong> {account.routing_number || '••••••••••'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Linked:</strong> {formatDate(account.created_at)}
                        </Typography>
                        {account.last_verified_at && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Verified:</strong> {formatDate(account.last_verified_at)}
                            </Typography>
                        )}
                    </Box>

                    {/* Status Chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={statusChip.label}
                            color={statusChip.color}
                            size="small"
                            icon={statusChip.icon}
                        />
                        {account.is_active && (
                            <Chip
                                label="Primary"
                                color="primary"
                                size="small"
                                icon={<PrimaryIcon fontSize="small" />}
                            />
                        )}
                    </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    {!account.is_active && (
                        <Tooltip title="Reactivate this account">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleAction('reactivate')}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </CardActions>
            </Card>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {account.is_active ? (
                    <>
                        {!account.is_primary && (
                            <MenuItem onClick={() => handleAction('setPrimary')}>
                                <PrimaryIcon sx={{ mr: 1 }} fontSize="small" />
                                Set as Primary
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => handleAction('deactivate')}>
                            <DeactivateIcon sx={{ mr: 1 }} fontSize="small" />
                            Deactivate
                        </MenuItem>
                        <MenuItem onClick={() => handleAction('delete')}>
                            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                            Delete
                        </MenuItem>
                    </>
                ) : (
                    <>
                        <MenuItem onClick={() => handleAction('reactivate')}>
                            <RefreshIcon sx={{ mr: 1 }} fontSize="small" />
                            Reactivate
                        </MenuItem>
                        <MenuItem onClick={() => handleAction('delete')}>
                            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                            Delete
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={handleCancel}>
                <DialogTitle>{dialogContent.title}</DialogTitle>
                <DialogContent>
                    <Typography>{dialogContent.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color={confirmAction === 'delete' ? 'error' : 'primary'}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default BankAccountCard;
